import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CaretLeft, MapPin, Calendar, CloudSun, Trash, MagnifyingGlass, Sparkle,
  ShareNetwork, TwitterLogo, FacebookLogo, Copy, Check, Binoculars, LinkSimple,
  Microphone, Waveform, Plus, X, WhatsappLogo, Info
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { API, categoryConfig } from "@/config/constants";
import { useAuth } from "@/context/AuthContext";

const EntryDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user;
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [identifying, setIdentifying] = useState(false);
  const [identifyFocus, setIdentifyFocus] = useState("auto");
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showINatDialog, setShowINatDialog] = useState(false);
  const [transcribingId, setTranscribingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [inatSearchQuery, setInatSearchQuery] = useState("");
  const [inatResults, setInatResults] = useState([]);
  const [searchingINat, setSearchingINat] = useState(false);
  const [selectedSpeciesForLink, setSelectedSpeciesForLink] = useState(null);

  useEffect(() => {
    const fetchEntry = async () => {
      try {
        const response = await axios.get(`${API}/entries/${id}`);
        setEntry(response.data);
      } catch (error) {
        toast.error("Entry not found");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    fetchEntry();
  }, [id, navigate]);

  const handleIdentifySpecies = async (photoPath) => {
    setIdentifying(true);
    try {
      const response = await axios.post(`${API}/identify-species?entry_id=${id}&photo_path=${photoPath}&focus=${identifyFocus}`);
      setEntry(prev => ({
        ...prev,
        species_identified: [...(prev.species_identified || []), response.data]
      }));
      toast.success(`Identified: ${response.data.species_name}`);
    } catch (error) {
      toast.error("Species identification failed");
    } finally {
      setIdentifying(false);
    }
  };

  const handleIdentifyAllPhotos = async () => {
    if (!entry?.photos?.length) return;
    setIdentifying(true);
    let successCount = 0;
    let failCount = 0;
    const newSpecies = [];
    for (const photo of entry.photos) {
      try {
        const response = await axios.post(`${API}/identify-species?entry_id=${id}&photo_path=${photo.storage_path}&focus=${identifyFocus}`);
        newSpecies.push(response.data);
        successCount++;
        toast.success(`Identified: ${response.data.species_name}`);
      } catch (error) {
        failCount++;
      }
    }
    if (newSpecies.length) {
      setEntry(prev => ({
        ...prev,
        species_identified: [...(prev.species_identified || []), ...newSpecies]
      }));
    }
    if (failCount && !successCount) toast.error("Species identification failed");
    setIdentifying(false);
  };

  const handleGenerateInsights = async () => {
    setGeneratingInsights(true);
    try {
      const response = await axios.post(`${API}/entries/${id}/generate-insights`);
      setEntry(prev => ({ ...prev, ai_insights: response.data.insight }));
      toast.success("Insights generated!");
    } catch (error) {
      toast.error("Failed to generate insights");
    } finally {
      setGeneratingInsights(false);
    }
  };

  const handleTranscribe = async (noteId) => {
    setTranscribingId(noteId);
    try {
      const response = await axios.post(`${API}/entries/${id}/voice-notes/${noteId}/transcribe`);
      setEntry(prev => ({
        ...prev,
        voice_notes: prev.voice_notes.map(vn =>
          vn.id === noteId ? { ...vn, transcription: response.data.transcription } : vn
        )
      }));
      toast.success("Transcription complete!");
    } catch (error) {
      toast.error("Transcription failed");
    } finally {
      setTranscribingId(null);
    }
  };

  const appendTranscriptionToContent = (text) => {
    const newContent = entry.content + "\n\n--- Voice Note ---\n" + text;
    axios.put(`${API}/entries/${id}`, { ...entry, content: newContent }).then(() => {
      setEntry(prev => ({ ...prev, content: newContent }));
      toast.success("Transcription appended to entry");
    }).catch(() => toast.error("Failed to update entry"));
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/entries/${id}`);
      toast.success("Entry deleted");
      navigate("/");
    } catch (error) {
      toast.error("Failed to delete entry");
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const handleShare = async () => {
    try {
      const response = await axios.get(`${API}/entries/${id}/share`);
      setShareData(response.data);
      setShowShareDialog(true);
    } catch (error) {
      toast.error("Failed to prepare share data");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareToTwitter = () => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(shareData?.twitter_text || "");
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, "_blank");
  };

  const shareToFacebook = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank");
  };

  const shareToWhatsApp = () => {
    const speciesList = entry?.species_identified?.map(s => s.species_name).join(", ");
    let message = `${shareData?.title || entry?.title}`;
    if (entry?.location) message += `\n📍 ${entry.location}`;
    if (speciesList) message += `\nSpecies: ${speciesList}`;
    if (shareData?.text) message += `\n\n${shareData.text}`;
    message += `\n\n${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareData?.title || entry?.title,
          text: shareData?.text,
          url: window.location.href
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          toast.error("Share failed");
        }
      }
    }
  };

  const searchINaturalist = async () => {
    if (!inatSearchQuery.trim()) return;
    setSearchingINat(true);
    try {
      const response = await axios.get(`${API}/inaturalist/search?query=${encodeURIComponent(inatSearchQuery)}`);
      setInatResults(response.data.results);
    } catch (error) {
      toast.error("iNaturalist search failed");
    } finally {
      setSearchingINat(false);
    }
  };

  const linkToINaturalist = async (taxonId) => {
    if (!selectedSpeciesForLink) return;
    try {
      await axios.post(`${API}/entries/${id}/species/${selectedSpeciesForLink.id}/link-inaturalist?taxon_id=${taxonId}`);
      const response = await axios.get(`${API}/entries/${id}`);
      setEntry(response.data);
      setShowINatDialog(false);
      setSelectedSpeciesForLink(null);
      setInatResults([]);
      setInatSearchQuery("");
      toast.success("Linked to iNaturalist!");
    } catch (error) {
      toast.error("Failed to link to iNaturalist");
    }
  };

  const openINatLinkDialog = (species) => {
    setSelectedSpeciesForLink(species);
    setInatSearchQuery(species.species_name);
    setShowINatDialog(true);
    setTimeout(() => {
      axios.get(`${API}/inaturalist/search?query=${encodeURIComponent(species.species_name)}`)
        .then(res => setInatResults(res.data.results))
        .catch(() => {});
    }, 100);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto page-content">
        <Skeleton className="h-8 w-48 mb-4 bg-[#EAE7DE]" />
        <Skeleton className="h-64 w-full mb-4 bg-[#EAE7DE]" />
        <Skeleton className="h-4 w-full mb-2 bg-[#EAE7DE]" />
        <Skeleton className="h-4 w-3/4 bg-[#EAE7DE]" />
      </div>
    );
  }

  if (!entry) return null;

  return (
    <div className="max-w-4xl mx-auto page-content" data-testid="entry-detail-page">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Button variant="ghost" onClick={() => navigate("/")} className="text-white" data-testid="back-btn">
          <CaretLeft size={20} /> Back
        </Button>
        <div className="flex-1" />
        <div className="flex items-center gap-1 sm:gap-2">
          <Button variant="outline" size="icon" onClick={handleShare} className="bg-[#55624F] border-[#55624F] text-white h-9 w-9" data-testid="share-btn">
            <ShareNetwork size={18} />
          </Button>
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" onClick={() => navigate(`/entry/${id}/edit`)} className="bg-[#55624F] border-[#55624F] text-white h-9 text-xs sm:text-sm" data-testid="edit-entry-btn">
                Edit
              </Button>
              <Button variant="outline" size="icon" onClick={() => setShowDeleteConfirm(true)} className="border-[#C25A3C] text-[#C25A3C] hover:bg-[#C25A3C] hover:text-white h-9 w-9" data-testid="delete-entry-btn">
                <Trash size={18} />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-white border-[#D2C2B3] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Delete Entry</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#55624F]">Are you sure you want to delete this entry? This action cannot be undone.</p>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="border-[#D2C2B3]" data-testid="cancel-delete-btn">Cancel</Button>
            <Button onClick={handleDelete} className="bg-[#C25A3C] hover:bg-[#A84B32] text-white" data-testid="confirm-delete-btn">
              <Trash size={16} className="mr-1" /> Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Title and Meta */}
      <h1 className="font-heading text-2xl sm:text-4xl font-semibold text-white mb-4">{entry.title}</h1>
      <div className="flex flex-wrap items-center gap-4 text-sm text-white/70 mb-6">
        {entry.location && (<span className="flex items-center gap-1"><MapPin size={16} weight="duotone" />{entry.location}</span>)}
        <span className="flex items-center gap-1"><Calendar size={16} weight="duotone" />{new Date(entry.created_at).toLocaleDateString()}</span>
        {entry.weather && (<span className="flex items-center gap-1"><CloudSun size={16} weight="duotone" />{entry.weather}</span>)}
      </div>

      {/* Tags */}
      {entry.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {entry.tags.map(tag => (<Badge key={tag} className="bg-[#55624F] text-white hover:bg-[#424D3D]">{tag}</Badge>))}
        </div>
      )}

      {/* Content */}
      <div className="prose prose-lg max-w-none mb-8">
        <p className="text-white whitespace-pre-wrap">{entry.content}</p>
      </div>

      {/* Photos */}
      {entry.photos?.length > 0 && (
        <div className="mb-8">
          <h2 className="font-heading text-2xl font-semibold text-white mb-4">Photos</h2>

          {/* AI Identification Controls — admin only */}
          {isAdmin && (
            <TooltipProvider delayDuration={150}>
              <div
                className="flex flex-wrap items-center gap-3 mb-4 px-4 py-3 rounded-lg bg-white/10 border border-white/20 backdrop-blur-sm"
                data-testid="ai-controls-bar"
              >
                <div className="flex items-center gap-2">
                  <Sparkle size={18} weight="duotone" className="text-[#F4C95D]" />
                  <span className="text-sm font-medium text-white">AI Species ID</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="text-white/60 hover:text-white transition-colors"
                        aria-label="What does AI Species ID do?"
                        data-testid="ai-focus-help"
                      >
                        <Info size={16} weight="duotone" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-[#2E332B] text-white border-[#55624F]">
                      <p className="text-xs leading-relaxed">
                        Pick what the AI should look for, then click <b>Identify Species</b>.
                        The AI will analyze every photo in this entry, name what it sees,
                        and list the results under <b>Species Identified</b>.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="flex items-center gap-2 ml-auto flex-wrap">
                  <span className="text-xs text-white/70">Focus:</span>
                  <Select value={identifyFocus} onValueChange={setIdentifyFocus}>
                    <SelectTrigger className="w-[140px] h-9 text-xs border-white/30 bg-white/10 text-white" data-testid="focus-selector">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto-detect</SelectItem>
                      <SelectItem value="bird">Birds</SelectItem>
                      <SelectItem value="plant">Plants</SelectItem>
                      <SelectItem value="wildlife">Wildlife</SelectItem>
                      <SelectItem value="insect">Insects</SelectItem>
                      <SelectItem value="fungi">Fungi</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleIdentifyAllPhotos}
                    disabled={identifying}
                    className="h-9 bg-[#C25A3C] hover:bg-[#A84B32] text-white text-sm"
                    data-testid="identify-all-btn"
                  >
                    {identifying ? (
                      <span className="animate-pulse-soft flex items-center gap-1">
                        <MagnifyingGlass size={14} /> Analyzing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <MagnifyingGlass size={14} /> Identify Species
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            </TooltipProvider>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {entry.photos.map(photo => (
              <div key={photo.id} className="relative group">
                <img src={`${API}/photos/${photo.storage_path}`} alt={photo.original_filename} className="w-full h-auto max-h-[500px] object-contain rounded-lg border border-[#D2C2B3] bg-white shadow-[0_6px_24px_rgba(50,50,50,0.5)]" />
                {isAdmin && (
                  <Button
                    onClick={() => handleIdentifySpecies(photo.storage_path)}
                    disabled={identifying}
                    size="sm"
                    className="absolute bottom-3 right-3 bg-black/60 hover:bg-[#C25A3C] text-white opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm border border-white/20 h-8 text-xs"
                    data-testid={`identify-btn-${photo.id}`}
                    title="Identify this photo only"
                  >
                    {identifying ? (<span className="animate-pulse-soft">Analyzing...</span>) : (<><MagnifyingGlass size={14} className="mr-1" />Identify this one</>)}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Species Identified */}
      {entry.species_identified?.length > 0 && (
        <div className="mb-8">
          <h2 className="font-heading text-2xl font-semibold text-white mb-4">Species Identified</h2>
          <div className="space-y-4">
            {entry.species_identified.map(species => {
              const spConfig = categoryConfig[species.category] || categoryConfig.unknown;
              const SpIcon = spConfig.icon;
              return (
                <Card key={species.id} className="bg-white border-[#D2C2B3] overflow-hidden shadow-[0_6px_24px_rgba(50,50,50,0.5)]">
                  <CardContent className="p-3 sm:p-4">
                    <div className="mb-2">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-heading text-lg sm:text-xl font-semibold text-[#2E332B] break-words min-w-0">{species.species_name}</h3>
                        <div className="text-right shrink-0 whitespace-nowrap">
                          <span className="text-xs text-[#7C8068] block">Confidence</span>
                          <span className="text-lg font-semibold text-[#55624F]">{(species.confidence * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge style={{ backgroundColor: spConfig.color }} className="text-white flex items-center gap-1 text-xs">
                          <SpIcon size={12} weight="bold" />{spConfig.label}
                        </Badge>
                        {species.inaturalist ? (
                          <a href={species.inaturalist.wikipedia_url || `https://www.inaturalist.org/taxa/${species.inaturalist.taxon_id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-[#55624F] hover:underline">
                            <Binoculars size={14} />iNaturalist Verified
                          </a>
                        ) : isAdmin ? (
                          <Button variant="ghost" size="sm" onClick={() => openINatLinkDialog(species)} className="text-xs text-[#7C8068] hover:text-[#55624F] h-6 px-2">
                            <LinkSimple size={14} className="mr-1" />Link to iNaturalist
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    {species.habitat && (<p className="text-xs text-[#7C8068] mb-1"><span className="font-medium">Habitat:</span> {species.habitat}</p>)}
                    <p className="text-sm text-[#55624F]">{species.description}</p>
                    {species.interesting_fact && (<p className="text-xs text-[#55624F] mt-2 italic font-medium">{species.interesting_fact}</p>)}
                    {species.inaturalist && (
                      <div className="mt-3 p-2 bg-[#EAE7DE] rounded-lg text-xs text-[#7C8068] break-words">
                        <span className="font-medium">Scientific name:</span> {species.inaturalist.scientific_name}
                        <span className="font-medium ml-0 sm:ml-2 block sm:inline"> · Community observations:</span> {species.inaturalist.observations_count?.toLocaleString()}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Voice Notes */}
      {entry.voice_notes?.length > 0 && (
        <div className="mb-8">
          <h2 className="font-heading text-2xl font-semibold text-white mb-4 flex items-center gap-2">
            <Waveform size={24} weight="duotone" className="text-[#55624F]" />Voice Notes
          </h2>
          <div className="space-y-3">
            {entry.voice_notes.map(vn => (
              <Card key={vn.id} className="bg-white border-[#D2C2B3] shadow-[0_6px_24px_rgba(50,50,50,0.5)]" data-testid={`voice-note-${vn.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Microphone size={18} className="text-[#55624F]" />
                    <audio controls src={`${API}/voice/${vn.storage_path}`} className="flex-1 h-10" data-testid={`audio-player-${vn.id}`} />
                    <span className="text-xs text-[#7C8068]">{new Date(vn.created_at).toLocaleTimeString()}</span>
                  </div>
                  {vn.transcription ? (
                    <div className="mt-2 p-3 bg-[#EAE7DE] rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-[#55624F]">Transcription</span>
                        {isAdmin && (
                          <Button variant="ghost" size="sm" onClick={() => appendTranscriptionToContent(vn.transcription)} className="text-xs text-[#C25A3C] hover:text-[#A84B32] h-6 px-2" data-testid={`append-transcription-${vn.id}`}>
                            <Plus size={12} className="mr-1" />Append to entry
                          </Button>
                        )}
                      </div>
                      <p className="text-sm text-[#55624F]">{vn.transcription}</p>
                    </div>
                  ) : isAdmin ? (
                    <Button onClick={() => handleTranscribe(vn.id)} disabled={transcribingId === vn.id} className="mt-2 bg-[#55624F] hover:bg-[#424D3D] text-white text-sm h-8" data-testid={`transcribe-btn-${vn.id}`}>
                      {transcribingId === vn.id ? (<span className="animate-pulse-soft flex items-center gap-1"><Waveform size={14} /> Transcribing...</span>) : (<span className="flex items-center gap-1"><Waveform size={14} /> Transcribe with AI</span>)}
                    </Button>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* AI Insights — only show section if there are insights OR admin is viewing */}
      {(entry.ai_insights || isAdmin) && (
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="font-heading text-2xl font-semibold text-white">AI Insights</h2>
            {isAdmin && (
              <Button onClick={handleGenerateInsights} disabled={generatingInsights} className="bg-[#55624F] hover:bg-[#424D3D] text-white text-sm" data-testid="generate-insights-btn">
                {generatingInsights ? (<span className="animate-pulse-soft">Generating...</span>) : (<><Sparkle size={16} className="mr-1" />{entry.ai_insights ? "Regenerate" : "Generate Insights"}</>)}
              </Button>
            )}
          </div>
          {entry.ai_insights ? (
            <Card className="bg-[rgba(30,30,30,0.7)] border-white/10 shadow-[0_6px_24px_rgba(50,50,50,0.5)]">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <img src="https://static.prod-images.emergentagent.com/jobs/d79f4e12-49d2-4273-980e-fb3c434a8417/images/063289cc16a6f951001ce66c1071e2228e26c122a5b28e1a275e5260efaf70fd.png" alt="AI" className="w-12 h-12 object-contain" />
                  <p className="text-white whitespace-pre-wrap">{entry.ai_insights}</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <p className="text-white/70 font-handwritten text-lg">Click "Generate Insights" to get AI-powered observations about your entry.</p>
          )}
        </div>
      )}

      {/* Map */}
      {entry.latitude && entry.longitude && (
        <div className="mb-8">
          <h2 className="font-heading text-2xl font-semibold text-white mb-4">Location</h2>
          <div className="h-64 rounded-lg overflow-hidden border border-[#D2C2B3]">
            <MapContainer center={[entry.latitude, entry.longitude]} zoom={13} className="h-full w-full">
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker position={[entry.latitude, entry.longitude]}>
                <Popup>{entry.location || entry.title}</Popup>
              </Marker>
            </MapContainer>
          </div>
        </div>
      )}

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-white border-[#D2C2B3]">
          <DialogHeader><DialogTitle className="font-heading text-xl">Share Entry</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            {shareData && (
              <>
                <div className="p-3 bg-[#EAE7DE] rounded-lg"><p className="text-sm text-[#2E332B]">{shareData.text}</p></div>
                <div className="flex gap-3 flex-wrap">
                  {navigator.share && (<Button onClick={nativeShare} className="flex-1 bg-[#55624F] hover:bg-[#424D3D] text-white"><ShareNetwork size={18} className="mr-2" />Share</Button>)}
                  <Button onClick={shareToWhatsApp} variant="outline" className="flex-1 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white" data-testid="share-whatsapp"><WhatsappLogo size={18} className="mr-2" />WhatsApp</Button>
                  <Button onClick={shareToTwitter} variant="outline" className="flex-1 border-[#1DA1F2] text-[#1DA1F2] hover:bg-[#1DA1F2] hover:text-white" data-testid="share-twitter"><TwitterLogo size={18} className="mr-2" />Twitter</Button>
                  <Button onClick={shareToFacebook} variant="outline" className="flex-1 border-[#4267B2] text-[#4267B2] hover:bg-[#4267B2] hover:text-white" data-testid="share-facebook"><FacebookLogo size={18} className="mr-2" />Facebook</Button>
                </div>
                <div className="flex gap-2">
                  <Input value={window.location.href} readOnly className="flex-1 bg-[#EAE7DE] border-[#D2C2B3] text-sm" />
                  <Button onClick={() => copyToClipboard(window.location.href)} variant="outline" className="border-[#D2C2B3]">{copied ? <Check size={18} className="text-green-600" /> : <Copy size={18} />}</Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* iNaturalist Link Dialog */}
      <Dialog open={showINatDialog} onOpenChange={(open) => { setShowINatDialog(open); if (!open) { setSelectedSpeciesForLink(null); setInatResults([]); setInatSearchQuery(""); } }}>
        <DialogContent className="bg-white border-[#D2C2B3] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Binoculars size={24} weight="duotone" className="text-[#55624F]" />Link to iNaturalist
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedSpeciesForLink && (<p className="text-sm text-[#7C8068]">Linking: <span className="font-medium text-[#2E332B]">{selectedSpeciesForLink.species_name}</span></p>)}
            <div className="flex gap-2">
              <Input value={inatSearchQuery} onChange={(e) => setInatSearchQuery(e.target.value)} placeholder="Search iNaturalist species..." onKeyPress={(e) => e.key === "Enter" && searchINaturalist()} className="flex-1 bg-[#EAE7DE] border-[#D2C2B3]" />
              <Button onClick={searchINaturalist} disabled={searchingINat} className="bg-[#55624F] hover:bg-[#424D3D] text-white">{searchingINat ? "..." : <MagnifyingGlass size={18} />}</Button>
            </div>
            {inatResults.length > 0 && (
              <div className="max-h-64 overflow-y-auto space-y-2">
                {inatResults.map(result => (
                  <Card key={result.id} className="cursor-pointer hover:bg-[#EAE7DE] transition-colors border-[#D2C2B3]" onClick={() => linkToINaturalist(result.id)}>
                    <CardContent className="p-3 flex items-center gap-3">
                      {result.photo_url && (<img src={result.photo_url} alt={result.name} className="w-12 h-12 object-cover rounded" />)}
                      <div className="flex-1">
                        <p className="font-medium text-[#2E332B]">{result.common_name}</p>
                        <p className="text-xs text-[#7C8068] italic">{result.name}</p>
                        <p className="text-xs text-[#7C8068]">{result.iconic_taxon_name} · {result.observations_count?.toLocaleString()} observations</p>
                      </div>
                      <LinkSimple size={20} className="text-[#55624F]" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            {inatResults.length === 0 && inatSearchQuery && !searchingINat && (<p className="text-sm text-[#7C8068] text-center py-4">No results found. Try a different search term.</p>)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EntryDetailPage;
